import { Check, ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// ---- PAÍSES ----

type Country = {
  code: string;
  name: string;
  available: boolean;
};

const COUNTRIES: Country[] = [
  { code: "PE", name: "Perú", available: true },
  { code: "CO", name: "Colombia", available: false },
  { code: "CL", name: "Chile", available: false },
  { code: "BR", name: "Brasil", available: false },
  { code: "AR", name: "Argentina", available: false },
];

const activeCountry = COUNTRIES.find((country) => country.available)!;
const upcomingCountries = COUNTRIES.filter((country) => !country.available);

// ---- BANDERAS ----

/** Banderas circulares simplificadas; solo se usan a tamaño de ícono. */
function CountryFlag({
  code,
  className,
}: {
  code: string;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={cn("h-4 w-4 shrink-0", className)}
      aria-hidden
    >
      <defs>
        <clipPath id={`flag-clip-${code}`}>
          <circle cx="12" cy="12" r="12" />
        </clipPath>
      </defs>
      <g clipPath={`url(#flag-clip-${code})`}>
        {code === "PE" && (
          <>
            <rect width="24" height="24" fill="#fff" />
            <rect width="8" height="24" fill="#D91023" />
            <rect x="16" width="8" height="24" fill="#D91023" />
          </>
        )}
        {code === "CO" && (
          <>
            <rect width="24" height="12" fill="#FCD116" />
            <rect y="12" width="24" height="6" fill="#003893" />
            <rect y="18" width="24" height="6" fill="#CE1126" />
          </>
        )}
        {code === "CL" && (
          <>
            <rect width="24" height="12" fill="#fff" />
            <rect y="12" width="24" height="12" fill="#D52B1E" />
            <rect width="12" height="12" fill="#0039A6" />
            <path
              d="M6 3l1.18 3.4 3.32.09-2.65 2.1.94 3.41L6 10.05 3.21 12l.94-3.41-2.65-2.1 3.32-.09z"
              fill="#fff"
            />
          </>
        )}
        {code === "BR" && (
          <>
            <rect width="24" height="24" fill="#009B3A" />
            <path d="M12 3l10 9-10 9L2 12z" fill="#FEDF00" />
            <circle cx="12" cy="12" r="4.4" fill="#002776" />
          </>
        )}
        {code === "AR" && (
          <>
            <rect width="24" height="24" fill="#fff" />
            <rect width="24" height="8" fill="#74ACDF" />
            <rect y="16" width="24" height="8" fill="#74ACDF" />
            <circle cx="12" cy="12" r="2.6" fill="#F6B40E" />
          </>
        )}
      </g>
      <circle
        cx="12"
        cy="12"
        r="11.5"
        fill="none"
        stroke="rgba(15,23,42,0.12)"
      />
    </svg>
  );
}

// ---- SWITCHER ----

/**
 * Selector de país del header. Hoy es informativo: solo Perú está disponible y
 * el resto anuncia la expansión regional.
 */
export function CountrySwitcher() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="group flex items-center gap-2 rounded-full border border-slate-100 bg-card py-1.5 pl-1.5 pr-3 shadow-sm transition-colors hover:bg-accent data-[state=open]:border-blue-500 dark:border-slate-800 dark:bg-slate-900">
          <CountryFlag code={activeCountry.code} className="h-5 w-5" />
          <span className="hidden text-sm font-bold text-slate-800 md:inline dark:text-slate-100">
            {activeCountry.name}
          </span>
          <span className="hidden rounded-full bg-blue-50 px-2 py-0.5 text-[9px] font-extrabold uppercase leading-none tracking-wide text-blue-600 lg:inline dark:bg-blue-950/40 dark:text-blue-300">
            +{upcomingCountries.length} pronto
          </span>
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-64 p-0">
        <div className="p-1">
          <DropdownMenuLabel className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
            Disponible
          </DropdownMenuLabel>
          <DropdownMenuItem className="cursor-default gap-2.5 py-2 font-bold">
            <CountryFlag code={activeCountry.code} />
            <span className="flex-1">{activeCountry.name}</span>
            <Check className="h-4 w-4 text-blue-600" />
          </DropdownMenuItem>

          <DropdownMenuLabel className="mt-1 text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
            Próximamente
          </DropdownMenuLabel>
          {upcomingCountries.map((country) => (
            <DropdownMenuItem
              key={country.code}
              disabled
              className="gap-2.5 py-2 opacity-100 data-disabled:opacity-100"
            >
              <CountryFlag code={country.code} className="opacity-60" />
              <span className="flex-1 font-medium text-slate-400">
                {country.name}
              </span>
              <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                Pronto
              </span>
            </DropdownMenuItem>
          ))}
        </div>
        <p className="border-t border-slate-100 px-3 py-2.5 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
          Estamos llevando LicitiGo a toda la región.
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
